// Fog of war (lobby setting, Design §9.2), drawn by the client only: what the own tank can't see
// lies under a light grey veil, and enemy tanks, bullets and enemy mines there are not drawn.
import type { Constants, Frame } from './render.js';

/** Lobby setting: 0 off, 1 obstacles cast shadows, this one also a view cone. */
const FOG_CONE = 2;

/** View cone in degrees per cannon (match shop byte 0): Präzision narrow, Schnellfeuer and Schrot wide. */
const CONE_DEG = [90, 90, 120, 60, 150, 90, 90, 90];
/** All round view at „An“: two tank lengths. */
const NEAR_RADII = 4;
const VEIL = '#c4c4c4';
const VEIL_ALPHA = 0.8;

/** What the own tank sees this frame. */
export interface Sight {
  x: number;
  y: number;
  angle: number;
  /** Half the cone in radians; ≥ π = all round. */
  half: number;
  /** All round within this distance. */
  near: number;
  /** Obstacles and barriers: per polygon corner count, then x, y per corner. */
  polygons: Float32Array[];
}

/** The own living tank's sight with fog on, else none (fog off, offline, spectator, dead). */
export function sight(f: Frame, c: Constants): Sight | undefined {
  if (!f.fog || f.you === undefined || f.you < 0) return undefined;
  const o = f.you * c.tankStride;
  if (f.tanks[o + 4] !== 1) return undefined;
  const cone = f.fog >= FOG_CONE;
  const cannon = f.loadouts?.[f.you * 4] ?? 0;
  return {
    x: f.tanks[o],
    y: f.tanks[o + 1],
    angle: f.tanks[o + 2],
    half: cone ? (((CONE_DEG[cannon] ?? 90) / 2) * Math.PI) / 180 : Math.PI,
    near: cone ? NEAR_RADII * c.tankRadius : Infinity,
    polygons: f.barriers ? [f.walls, f.barriers] : [f.walls],
  };
}

/** Whether something at `x`, `y` (radius `r`: its centre or a point on its rim) is in sight; always without fog. */
export function sees(s: Sight | undefined, x: number, y: number, r = 0): boolean {
  if (!s) return true;
  if (seesPoint(s, x, y)) return true;
  return r > 0 && (seesPoint(s, x + r, y) || seesPoint(s, x - r, y) || seesPoint(s, x, y + r) || seesPoint(s, x, y - r));
}

function seesPoint(s: Sight, x: number, y: number): boolean {
  const dx = x - s.x;
  const dy = y - s.y;
  if (dx * dx + dy * dy > s.near * s.near) {
    let off = Math.atan2(dy, dx) - s.angle;
    off = Math.atan2(Math.sin(off), Math.cos(off));
    if (Math.abs(off) > s.half) return false;
  }
  for (const walls of s.polygons) {
    for (let i = 0; i < walls.length; ) {
      const corners = walls[i++];
      for (let k = 0; k < corners; k++) {
        const a = i + 2 * k;
        const b = i + 2 * ((k + 1) % corners);
        if (crosses(s.x, s.y, x, y, walls[a], walls[a + 1], walls[b], walls[b + 1])) return false;
      }
      i += 2 * corners;
    }
  }
  return true;
}

/** Whether segment p→q crosses segment a→b. */
function crosses(px: number, py: number, qx: number, qy: number, ax: number, ay: number, bx: number, by: number): boolean {
  const side = (ux: number, uy: number, vx: number, vy: number, wx: number, wy: number) => (vx - ux) * (wy - uy) - (vy - uy) * (wx - ux);
  const d1 = side(ax, ay, bx, by, px, py);
  const d2 = side(ax, ay, bx, by, qx, qy);
  const d3 = side(px, py, qx, qy, ax, ay);
  const d4 = side(px, py, qx, qy, bx, by);
  return d1 * d2 < 0 && d3 * d4 < 0;
}

let layer: HTMLCanvasElement | undefined;

/**
 * The veil over everything out of sight, for the camera `scale`, `ox`, `oy` on a `w`×`h` canvas:
 * drawn opaque on its own layer (overlapping shadows stay one shade), then laid over the map.
 */
export function drawFog(ctx: CanvasRenderingContext2D, s: Sight, f: Frame, w: number, h: number, scale: number, ox: number, oy: number): void {
  layer ??= document.createElement('canvas');
  if (layer.width !== w || layer.height !== h) {
    layer.width = w;
    layer.height = h;
  }
  const g = layer.getContext('2d')!;
  g.setTransform(1, 0, 0, 1, 0, 0);
  g.clearRect(0, 0, w, h);
  g.setTransform(scale, 0, 0, scale, ox, oy);
  g.save();
  g.beginPath();
  g.rect(0, 0, f.worldWidth, f.worldHeight);
  g.clip();
  g.fillStyle = VEIL;
  const far = 2 * (f.worldWidth + f.worldHeight);
  if (s.half < Math.PI) {
    // Everything, then cut out the cone and the circle around the tank.
    g.fillRect(0, 0, f.worldWidth, f.worldHeight);
    g.globalCompositeOperation = 'destination-out';
    g.beginPath();
    g.moveTo(s.x, s.y);
    g.arc(s.x, s.y, far, s.angle - s.half, s.angle + s.half);
    g.closePath();
    g.moveTo(s.x + s.near, s.y);
    g.arc(s.x, s.y, s.near, 0, Math.PI * 2);
    g.fill();
    g.globalCompositeOperation = 'source-over';
  }
  // Shadows: each edge facing away from the tank, stretched away from it; the obstacle itself stays clear.
  for (const walls of s.polygons) {
    for (let i = 0; i < walls.length; ) {
      const corners = walls[i++];
      let area = 0;
      for (let k = 0; k < corners; k++) {
        const a = i + 2 * k;
        const b = i + 2 * ((k + 1) % corners);
        area += walls[a] * walls[b + 1] - walls[b] * walls[a + 1];
      }
      g.beginPath();
      for (let k = 0; k < corners; k++) {
        const [ax, ay] = [walls[i + 2 * k], walls[i + 2 * k + 1]];
        const b = i + 2 * ((k + 1) % corners);
        const [bx, by] = [walls[b], walls[b + 1]];
        const facing = (bx - ax) * (s.y - ay) - (by - ay) * (s.x - ax);
        if (facing * area <= 0) continue; // faces the tank
        const [fax, fay] = away(s, ax, ay, far);
        const [fbx, fby] = away(s, bx, by, far);
        g.moveTo(ax, ay);
        g.lineTo(bx, by);
        g.lineTo(fbx, fby);
        g.lineTo(fax, fay);
        g.closePath();
      }
      g.fill();
      i += 2 * corners;
    }
  }
  g.restore();
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = VEIL_ALPHA;
  ctx.drawImage(layer, 0, 0);
  ctx.restore();
}

/** Point `x`, `y` pushed `far` away from the tank. */
function away(s: Sight, x: number, y: number, far: number): [number, number] {
  const [dx, dy] = [x - s.x, y - s.y];
  const len = Math.hypot(dx, dy) || 1;
  return [x + (dx / len) * far, y + (dy / len) * far];
}
